import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { Table, TableSize } from '@ui/molecules/Table';
import { translate } from '@utils/i18n';
import { Button } from '@ui';
import { InlineSelect } from '@ui/molecules/inputs/inline/InlineSelect';
import toast from 'react-hot-toast';
import TechnicianModal from '../TechnicianModal';
import useUpdateAIInspectionManager from '../../hooks/useUpdateAIInspectionManager';
import useUpdateTechnicianIaModeOverride from '../../hooks/useUpdateTechnicianIaModeOverride';
import WorkflowModeTab from './WorkflowModeTab';
import './UserAccessStyles.scss';
const namespace = 'features.settings.ai_inspection_manager';
const IA_MODE_OVERRIDE_OPTIONS = [
    { value: '', label: translate('workflow_override_default', { namespace }) },
    { value: '0', label: translate('workflow_mode.tech_required_label', { namespace }) },
    { value: '1', label: translate('workflow_mode.tech_optional_label', { namespace }) },
    { value: '2', label: translate('workflow_mode.backoffice_first_label', { namespace }) }
];
function WorkflowOverrideCell({ item }) {
    const { updateTechnicianOverride } = useUpdateTechnicianIaModeOverride();
    const serverValue = item.iaModeOverride == null ? '' : String(item.iaModeOverride);
    const [localValue, setLocalValue] = useState(serverValue);
    const handleChange = useCallback((value) => {
        const previous = localValue;
        setLocalValue(value);
        const iaModeOverride = value === '' ? null : Number(value);
        updateTechnicianOverride.mutate({ technicianId: item.id, iaModeOverride }, {
            onSuccess: () => {
                toast.success(translate('workflow_override_update_success', { namespace }));
            },
            onError: () => {
                setLocalValue(previous);
                toast.error(translate('workflow_override_update_error', { namespace }));
            }
        });
    }, [item.id, localValue, updateTechnicianOverride]);
    return (React.createElement(InlineSelect, { options: IA_MODE_OVERRIDE_OPTIONS, value: localValue, onChange: handleChange, disabled: updateTechnicianOverride.isLoading }));
}
function UserAccess({ settingsData }) {
    var _a;
    const { updateSettings } = useUpdateAIInspectionManager();
    const [techniciansSelected, setTechniciansSelected] = useState((_a = settingsData === null || settingsData === void 0 ? void 0 : settingsData.technicians) !== null && _a !== void 0 ? _a : []);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const enabledTechnicians = useMemo(() => techniciansSelected.filter((u) => u.enabled), [techniciansSelected]);
    const filteredTechnicians = useMemo(() => {
        const query = searchQuery.trim().toLowerCase();
        if (!query)
            return enabledTechnicians;
        return enabledTechnicians.filter((t) => t.name.toLowerCase().includes(query));
    }, [enabledTechnicians, searchQuery]);
    const onSave = (technicians) => {
        const techniciansBlacklist = technicians
            .filter((technician) => !technician.enabled)
            .map((technician) => technician.id);
        updateSettings.mutate({
            settingsUpdate: {
                aiInspectionManagerTechniciansBlacklist: techniciansBlacklist
            }
        }, {
            onSuccess: () => {
                toast.success(translate('update_success', { namespace }));
            },
            onError: (updateError) => {
                toast.error(updateError.message || 'Error updating settings');
            }
        });
    };
    const onTechniciansSave = (selectedTechnicians) => {
        setTechniciansSelected(selectedTechnicians);
        onSave(selectedTechnicians);
    };
    useEffect(() => {
        if (settingsData === null || settingsData === void 0 ? void 0 : settingsData.technicians) {
            setTechniciansSelected(settingsData.technicians);
        }
    }, [settingsData === null || settingsData === void 0 ? void 0 : settingsData.technicians]);
    return (React.createElement("div", { className: "user-access-tab" },
        React.createElement("section", { className: "workspace__section" },
            React.createElement("header", { className: "section__title" },
                React.createElement("h2", { className: "section__label" }, translate('workflow_section_title', { namespace })),
                React.createElement("hr", { className: "section__split" })),
            React.createElement(WorkflowModeTab, null)),
        React.createElement("section", { className: "workspace__section" },
            React.createElement("header", { className: "section__title" },
                React.createElement("h2", { className: "section__label" }, translate('access_section_title', { namespace })),
                React.createElement("hr", { className: "section__split" }),
                React.createElement(Button, { onClick: () => {
                        setIsModalOpen(true);
                    } },
                    React.createElement("i", { className: "fa-light fa-pencil", "aria-hidden": "true" }),
                    translate('manage_technicians', { namespace }))),
            React.createElement("div", { className: "technician-table-toolbar" },
                React.createElement("div", { className: "qmb-input--search", style: { width: '200px', zIndex: 0 } },
                    React.createElement("input", { placeholder: translate('search_placeholder', { namespace }), value: searchQuery, onChange: (e) => setSearchQuery(e.target.value), "aria-label": translate('search_placeholder', { namespace }) }),
                    React.createElement("i", { className: "fa-light fa-search", "aria-hidden": "true" }))),
            React.createElement(Table, { columns: [
                    {
                        label: translate('column_title', { namespace }),
                        dataKey: 'name',
                        columnType: 'primary',
                        width: '100%',
                        renderer: ({ item }) => React.createElement("a", { href: `/technicians/${item.id}/edit` }, item.name)
                    },
                    {
                        label: translate('workflow_override_column', { namespace }),
                        dataKey: 'id',
                        width: 'fit-content',
                        renderer: ({ item }) => React.createElement(WorkflowOverrideCell, { item: item })
                    }
                ], data: filteredTechnicians, size: TableSize.Full, variant: "detail", paginated: true, pageSize: 25 })),
        isModalOpen && (React.createElement(TechnicianModal, { isModalOpen: isModalOpen, onClose: () => setIsModalOpen(false), initialTechnicians: techniciansSelected, onSave: onTechniciansSave }))));
}
export default UserAccess;
